import { useCallback, useEffect, useState } from 'react'
import { authErrors, authLoading, refreshTokenIsValid } from '@/reducers'
import {
  passwordResetLoading as passwordResetLoadingSelector,
  passwordResetComplete as passwordResetCompleteSelector,
  passwordResetErrors as passwordResetErrorsSelector,
  otpRequestLoading as otpRequestLoadingSelector,
} from '@/reducers/auth'
import {
  login,
  resetPassword,
  lookupEmail,
  requestOtp,
  verifyOtp,
} from '@/actions/auth'
import { useAppDispatch, useAppSelector, useUrlSearchParam } from '@/hooks'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import {
  redirectToAppleOAuthURL,
  redirectToGoogleOAuthURL,
} from '@/utils/oauth/oauthUtils'
import { logEvent } from '@/logging'
import { trackSignupStarted, trackSignupSubmitted } from '@/logging/segment'
import useRemoteConfig from '@/hooks/useRemoteConfig'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

type Mode = 'signup' | 'login' | 'forgotPassword' | 'otp' | 'impersonate'

/**
 * Which surface is asking the user for an OTP. Determines what the verify
 * button reads, what title is shown, and (eventually) which captcha action
 * we tag the request with.
 */
type OtpFlow = 'login' | 'signup'

type SignupForm = {
  name: string
  username: string
  referral_code?: string
}

type LoginForm = {
  username: string
  password?: string
}

type ForgotPasswordForm = {
  email: string
}

const signupSchema = yup.object({
  name: yup.string().required('Please enter your full name').trim(),
  username: yup
    .string()
    .required('Please enter your email address')
    .email('Please enter your email address'),
  referral_code: yup.string().optional(),
})

// Password is intentionally not required: the email-first flow looks the
// account up before deciding whether to ask for a password at all.
const loginSchema = yup.object({
  username: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
  password: yup.string().optional(),
})

const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address'),
})

export default function useProgramSignupSheetViewModel(
  programId: number | null,
  totalRewardsDollars: number,
  initialMode: Mode = 'signup',
) {
  const isGeneric = programId === null
  const dispatch = useAppDispatch()
  const paramReferralCode = useUrlSearchParam('referral_code') as string
  const { appRemoteConfig } = useRemoteConfig()
  const useCaptcha = !appRemoteConfig?.getBoolean('hide_signup_captcha')
  const { executeRecaptcha } = useGoogleReCaptcha()

  const isMobile =
    typeof window !== 'undefined' &&
    !window.matchMedia('(min-width: 768px)').matches
  const [mode, setMode] = useState<Mode>(initialMode)
  const [isSheetOpen, setIsSheetOpen] = useState(
    initialMode === 'login' && isMobile,
  )
  const [showReferralCode, setShowReferralCode] = useState(false)
  const [resetLinkSent, setResetLinkSent] = useState(false)
  const [captchaLoading, setCaptchaLoading] = useState(false)
  const [signupSuccess, setSignupSuccess] = useState(false)

  // OTP flow state
  /** When true on the login screen, we've looked the email up and the
   *  account has a usable password — show the password field. Forced true
   *  for impersonate mode so both fields appear immediately. */
  const [loginHasPassword, setLoginHasPassword] = useState(
    initialMode === 'impersonate',
  )
  /** The exact (lowercased) email value that was last looked up. If the
   *  user edits the email field after lookup we re-run the lookup before
   *  doing anything else. */
  const [loginLookedUpEmail, setLoginLookedUpEmail] = useState<string | null>(
    null,
  )
  const [emailLookupLoading, setEmailLookupLoading] = useState(false)
  const [otpFlow, setOtpFlow] = useState<OtpFlow>('login')
  const [otpEmail, setOtpEmail] = useState('')
  /** Signup-only fields captured at submit time and forwarded to the verify
   *  endpoint so the brand-new user gets the same name/referral/program
   *  hydration as the legacy password signup flow. */
  const [otpSignupName, setOtpSignupName] = useState('')
  const [otpSignupReferralCode, setOtpSignupReferralCode] = useState('')
  const [otpVerifyError, setOtpVerifyError] = useState<string | null>(null)
  const [otpResetSignal, setOtpResetSignal] = useState(0)
  /** Bumps when the user clicks "Resend" — drives the green confirmation
   *  banner inside `OtpCodeEntry`. */
  const [otpResendCount, setOtpResendCount] = useState(0)

  const authenticated = useAppSelector((state) => refreshTokenIsValid(state))
  const errors = useAppSelector((state) => authErrors(state))
  const loading = useAppSelector((state) => authLoading(state))
  const passwordResetLoading = useAppSelector((state) =>
    passwordResetLoadingSelector(state.auth),
  )
  const passwordResetComplete = useAppSelector((state) =>
    passwordResetCompleteSelector(state.auth),
  )
  const passwordResetErrors = useAppSelector((state) =>
    passwordResetErrorsSelector(state.auth),
  )
  const otpRequestLoading = useAppSelector((state) =>
    otpRequestLoadingSelector(state.auth),
  )

  const signupForm = useForm<SignupForm>({
    mode: 'onChange',
    resolver: yupResolver(signupSchema),
    defaultValues: {
      name: '',
      username: '',
      referral_code: paramReferralCode || '',
    },
  })

  const loginForm = useForm<LoginForm>({
    mode: 'onChange',
    resolver: yupResolver(loginSchema),
    defaultValues: { username: '', password: '' },
  })

  const forgotPasswordForm = useForm<ForgotPasswordForm>({
    mode: 'onChange',
    resolver: yupResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  })

  useEffect(() => {
    logEvent('start_sign_up')
    trackSignupStarted()
  }, [])

  useEffect(
    function onReferralCodeParam() {
      if (paramReferralCode) {
        signupForm.setValue('referral_code', paramReferralCode)
      }
    },
    [paramReferralCode, signupForm],
  )

  // Track password reset completion
  useEffect(
    function onPasswordResetComplete() {
      if (passwordResetComplete) {
        setResetLinkSent(true)
        const email = forgotPasswordForm.getValues('email')
        forgotPasswordForm.reset({ email })
      }
    },
    [passwordResetComplete, forgotPasswordForm],
  )

  const getCaptchaToken = useCallback(async () => {
    if (!executeRecaptcha) return null
    return await executeRecaptcha('signup')
  }, [executeRecaptcha])

  const openSheet = useCallback(() => setIsSheetOpen(true), [])
  const closeSheet = useCallback(() => setIsSheetOpen(false), [])

  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode)
    if (newMode !== 'forgotPassword') {
      setResetLinkSent(false)
    }
    // Switching away from login/impersonate resets the email lookup so we
    // don't show a stale password field on the next visit.
    if (newMode !== 'login' && newMode !== 'impersonate') {
      setLoginHasPassword(false)
      setLoginLookedUpEmail(null)
    }
    // Switching away from OTP wipes the verify error so it doesn't follow
    // the user back to the entry screen.
    if (newMode !== 'otp') {
      setOtpVerifyError(null)
    }
  }, [])

  /**
   * Drives the OTP request for either signup or login. Used by both
   * `onSignupSubmit` and the login flow's "Send code" path.
   */
  const dispatchOtpRequest = useCallback(
    async (
      email: string,
      requestType: 'login' | 'signup' = 'login',
    ): Promise<{ throttled: boolean }> => {
      const captcha = executeRecaptcha
        ? (await executeRecaptcha('otp_request')) ?? ''
        : ''
      const result = (await dispatch(
        requestOtp({ email, captcha, requestType }),
      )) as {
        error?: boolean
        payload?: { status?: number }
      }
      if (result?.error && result.payload?.status === 429) {
        return { throttled: true }
      }
      return { throttled: false }
    },
    [dispatch, executeRecaptcha],
  )

  const onSignupSubmit = useCallback(
    async (data: SignupForm) => {
      // We still resolve a captcha token here for legacy callers but the OTP
      // flow doesn't need it on the signup-form submit step itself — the
      // captcha gating happens on the OTP request and verify endpoints.
      if (useCaptcha) {
        setCaptchaLoading(true)
        await getCaptchaToken()
        setCaptchaLoading(false)
      }

      trackSignupSubmitted('email')
      setSignupSuccess(true)

      const email = data.username.toLowerCase()
      setOtpEmail(email)
      setOtpFlow('signup')
      // Stash the signup-form fields so the verify call can forward them to
      // the BE — this mirrors the legacy /api/users/ hydration path (name
      // split into first/last, referral code processed, utility program
      // pre-confirmed on the brand-new profile).
      setOtpSignupName(data.name)
      setOtpSignupReferralCode(data.referral_code ?? '')
      setLoginHasPassword(false)

      // Route to the OTP screen immediately so the user isn't waiting on the
      // request — the request fires in the background and the OTP screen's
      // loading state will show until it resolves.
      handleModeChange('otp')
      const { throttled } = await dispatchOtpRequest(email, 'signup')
      if (throttled) {
        setOtpVerifyError('Too many requests. Please try again later.')
      }
    },
    [useCaptcha, getCaptchaToken, dispatchOtpRequest, handleModeChange],
  )

  /**
   * Handles the email-first login submit. If the lookup says the account
   * has a usable password, reveal the password field and stay on the login
   * screen. Otherwise fire an OTP and route to the OTP entry screen.
   *
   * If the user has already revealed the password field and typed something
   * in, this is the password-login submit — fall through to the legacy
   * `login` action.
   */
  /**
   * Looks up an email and either reveals the password field (account has a
   * password) or fires an OTP and routes to the OTP screen. Returns true if
   * the lookup determined the account has a password.
   */
  const lookupAndBranch = useCallback(
    async (email: string): Promise<boolean> => {
      setEmailLookupLoading(true)
      const result = (await dispatch(lookupEmail(email))) as {
        has_password: boolean
        otp_available: boolean
        throttled?: boolean
      } | null
      setEmailLookupLoading(false)

      if (result?.throttled) {
        loginForm.setError('username', {
          message: 'Too many attempts. Please try again later.',
        })
        return false
      }

      setLoginLookedUpEmail(email)

      if (result?.has_password) {
        setLoginHasPassword(true)
        return true
      }

      // No password (or unknown email) → route to OTP immediately and
      // fire the request in the background. The OTP screen's loading state
      // will hold until the request resolves.
      setLoginHasPassword(false)
      setOtpEmail(email)
      setOtpFlow('login')
      // Clear any stashed signup-form values — on the login path there's
      // no name/referral code to forward, and we don't want leftover state
      // from a prior signup attempt leaking into the verify call.
      setOtpSignupName('')
      setOtpSignupReferralCode('')
      handleModeChange('otp')
      const { throttled } = await dispatchOtpRequest(email)
      if (throttled) {
        setOtpVerifyError('Too many requests. Please try again later.')
      }
      return false
    },
    [dispatch, dispatchOtpRequest, handleModeChange, loginForm],
  )

  /**
   * Bypass the password field and jump straight to OTP for the currently
   * looked-up email. Used by the "Send Code Instead" link on the login
   * screen when the account has a password but the user prefers OTP.
   */
  const onSendCodeInstead = useCallback(async () => {
    const email = loginForm.getValues('username')?.toLowerCase()
    if (!email) return
    setLoginHasPassword(false)
    setOtpEmail(email)
    setOtpFlow('login')
    setOtpSignupName('')
    setOtpSignupReferralCode('')
    handleModeChange('otp')
    const { throttled } = await dispatchOtpRequest(email)
    if (throttled) {
      setOtpVerifyError('Too many requests. Please try again later.')
    }
  }, [dispatchOtpRequest, handleModeChange, loginForm])

  const onLoginSubmit = useCallback(
    async (data: LoginForm) => {
      const email = data.username.toLowerCase()

      // Impersonate mode: skip the email lookup entirely — both fields are
      // always visible and we go straight to password auth.
      if (mode === 'impersonate') {
        if (!data.password) {
          loginForm.setError('password', {
            message: 'Please enter your password.',
          })
          return
        }
        dispatch(login(email, data.password))
        return
      }

      const emailMatchesLookup = loginLookedUpEmail === email

      // Password branch — only valid if the password field is showing AND
      // the email hasn't been edited since the lookup that revealed it.
      if (loginHasPassword && emailMatchesLookup) {
        if (!data.password) {
          loginForm.setError('password', {
            message: 'Please enter your password.',
          })
          return
        }
        dispatch(login(email, data.password))
        return
      }

      // Email changed (or never looked up): re-run lookup. If the new
      // account has a password the field will reveal next render; the user
      // then re-submits to log in. If it doesn't, lookupAndBranch already
      // routed to the OTP screen.
      await lookupAndBranch(email)
    },
    [
      dispatch,
      loginForm,
      loginHasPassword,
      loginLookedUpEmail,
      lookupAndBranch,
      mode,
    ],
  )

  const onVerifyOtp = useCallback(
    async (code: string) => {
      setOtpVerifyError(null)
      const captcha = executeRecaptcha
        ? (await executeRecaptcha('otp_verify')) ?? ''
        : ''
      const result = (await dispatch(
        verifyOtp({
          email: otpEmail,
          code,
          captcha,
          // Signup-only fields. Empty on the login path; the BE only
          // applies them when creating a brand-new account. Utility
          // program is intentionally not forwarded — enrollment is
          // handled in-app after auth.
          name: otpFlow === 'signup' ? otpSignupName : '',
          referral_code: otpFlow === 'signup' ? otpSignupReferralCode : '',
        }),
      )) as {
        error?: boolean
        payload?: {
          status?: number
          detail?: string
          response?: { detail?: string }
        }
      }

      if (result?.error) {
        if (result.payload?.status === 429) {
          setOtpVerifyError('Too many attempts. Please try again later.')
        } else {
          const detail =
            result.payload?.response?.detail ?? result.payload?.detail
          setOtpVerifyError(
            detail || "That code doesn't match, please try again.",
          )
        }
        // Clear the boxes so the user can try again.
        setOtpResetSignal((s) => s + 1)
      }
    },
    [
      dispatch,
      executeRecaptcha,
      otpEmail,
      otpFlow,
      otpSignupName,
      otpSignupReferralCode,
    ],
  )

  const onResendOtp = useCallback(async () => {
    if (!otpEmail) return
    setOtpVerifyError(null)
    const requestType = otpFlow === 'signup' ? 'signup' : 'login'
    const { throttled } = await dispatchOtpRequest(otpEmail, requestType)
    if (throttled) {
      setOtpVerifyError('Too many requests. Please try again later.')
      return
    }
    setOtpResendCount((c) => c + 1)
    setOtpResetSignal((s) => s + 1)
  }, [dispatchOtpRequest, otpEmail, otpFlow])

  const onBackFromOtp = useCallback(() => {
    // Hop back to whichever screen kicked off the OTP, with the email value
    // preserved so the user can confirm/edit it.
    if (otpFlow === 'login') {
      loginForm.setValue('username', otpEmail)
    } else {
      signupForm.setValue('username', otpEmail)
    }
    handleModeChange(otpFlow)
  }, [handleModeChange, loginForm, otpEmail, otpFlow, signupForm])

  const onResetPassword = useCallback(
    async (data: ForgotPasswordForm) => {
      dispatch(resetPassword(data.email.toLowerCase()))
    },
    [dispatch],
  )

  const continueWithOAuth = useCallback(
    async (
      provider: 'google' | 'apple',
      redirect: (referralCode?: string) => Promise<void>,
    ) => {
      logEvent('signUp')
      trackSignupSubmitted(provider)
      setSignupSuccess(true)
      await new Promise((resolve) => setTimeout(resolve, 100))
      const referral_code = signupForm.getValues('referral_code')
      await redirect(referral_code)
    },
    [signupForm],
  )

  const continueWithGoogle = useCallback(
    () => continueWithOAuth('google', redirectToGoogleOAuthURL),
    [continueWithOAuth],
  )

  const continueWithApple = useCallback(
    () => continueWithOAuth('apple', redirectToAppleOAuthURL),
    [continueWithOAuth],
  )

  const getSignupFieldError = useCallback(
    (fieldName: keyof SignupForm) =>
      signupForm.formState.errors[fieldName]?.message,
    [signupForm.formState.errors],
  )

  /**
   * Mirrors the legacy `HeroSectionLogin1` behavior: when the login endpoint
   * returns the standard "no active account" 401, surface the failure as
   * inline field errors on email + password instead of as a top banner.
   */
  const loginInvalid =
    (errors?.detail as string) ===
    'No active account found with the given credentials'

  const displayedAuthErrors = loginInvalid
    ? { ...errors, detail: undefined }
    : errors

  const getLoginFieldError = useCallback(
    (fieldName: keyof LoginForm) => {
      const yupError = loginForm.formState.errors[fieldName]?.message
      if (yupError) return yupError
      if (loginInvalid && fieldName === 'password') {
        return 'Your email or password is incorrect. Please try again.'
      }
      return undefined
    },
    [loginForm.formState.errors, loginInvalid],
  )

  return {
    // Mode
    mode,
    setMode: handleModeChange,

    // Sheet control
    isSheetOpen,
    openSheet,
    closeSheet,

    // Signup
    signupForm,
    onSignupSubmit,
    getSignupFieldError,
    signupSuccess,

    // Login
    loginForm,
    onLoginSubmit,
    onSendCodeInstead,
    getLoginFieldError,
    loginHasPassword,
    emailLookupLoading,

    // OTP
    otpEmail,
    otpFlow,
    otpVerifyError,
    otpResetSignal,
    otpResendCount,
    otpRequestLoading:
      typeof otpRequestLoading !== 'undefined' && otpRequestLoading,
    onVerifyOtp,
    onResendOtp,
    onBackFromOtp,

    // Forgot password
    forgotPasswordForm,
    onResetPassword,
    resetLinkSent,
    passwordResetLoading,
    passwordResetErrors,

    // OAuth
    continueWithGoogle,
    continueWithApple,

    // Referral
    showReferralCode,
    toggleReferralCode: () => setShowReferralCode((prev) => !prev),
    paramReferralCode,

    // Auth state
    authenticated,
    errors: displayedAuthErrors,
    loading: typeof loading !== 'undefined' && loading,
    captchaLoading,
    executeRecaptcha,
    totalRewardsDollars,
    isGeneric,
  }
}

export type ProgramSignupSheetViewModel = ReturnType<
  typeof useProgramSignupSheetViewModel
>
